'use client'

import { useId, useRef, useState } from 'react'
import { motion, AnimatePresence } from 'motion/react'
import posthog from 'posthog-js'
import { useForm } from 'react-hook-form'
import ReCAPTCHA from 'react-google-recaptcha'
import { FadeIn } from '@/components/FadeIn'
import ContactButton from '@/components/ContactButton'
import AlertMessage from '@/components/AlertMessage'

// ---------------------------------------------------------------------------
// Reusable form primitives
// ---------------------------------------------------------------------------

function TextInput({
  label,
  register,
  name,
  validationRules,
  errors,
  placeholder: customPlaceholder,
  ...props
}) {
  let id = useId()
  let placeholderText = customPlaceholder || label

  return (
    <div className="group relative z-0 transition-all focus-within:z-10">
      <input
        type="text"
        id={id}
        {...register(name, validationRules)}
        {...props}
        placeholder={placeholderText}
        aria-label={label}
        aria-invalid={errors[name] ? 'true' : 'false'}
        aria-describedby={errors[name] ? `${id}-error` : undefined}
        className="peer block w-full border border-neutral-300 bg-transparent px-6 pt-12 pb-4 text-base/6 text-neutral-950 ring-4 ring-transparent transition placeholder:text-transparent focus:placeholder:text-neutral-400 group-first:rounded-t-2xl group-last:rounded-b-2xl focus:border-neutral-950 focus:ring-neutral-950/5 focus:outline-hidden"
      />
      <label
        htmlFor={id}
        className="pointer-events-none absolute top-1/2 left-6 -mt-3 origin-left text-base/6 text-neutral-500 transition-all duration-200 peer-not-placeholder-shown:-translate-y-4 peer-not-placeholder-shown:scale-75 peer-not-placeholder-shown:font-semibold peer-not-placeholder-shown:text-neutral-950 peer-focus:-translate-y-4 peer-focus:scale-75 peer-focus:font-semibold peer-focus:text-neutral-950"
      >
        {label}
      </label>
      {errors[name] && (
        <p
          id={`${id}-error`}
          className="mt-1 text-sm text-red-500"
          role="alert"
        >
          {errors[name].message}
        </p>
      )}
    </div>
  )
}

function TextAreaInput({
  label,
  register,
  name,
  validationRules,
  errors,
  rows = 4,
  placeholder: customPlaceholder,
  ...props
}) {
  let id = useId()
  let placeholderText = customPlaceholder || label

  return (
    <div className="group relative z-0 transition-all focus-within:z-10">
      <textarea
        id={id}
        {...register(name, validationRules)}
        {...props}
        rows={rows}
        placeholder={placeholderText}
        aria-label={label}
        aria-invalid={errors[name] ? 'true' : 'false'}
        aria-describedby={errors[name] ? `${id}-error` : undefined}
        className="peer block w-full resize-y border border-neutral-300 bg-transparent px-6 pt-12 pb-4 text-base/6 text-neutral-950 ring-4 ring-transparent transition placeholder:text-transparent focus:placeholder:text-neutral-400 group-first:rounded-t-2xl group-last:rounded-b-2xl focus:border-neutral-950 focus:ring-neutral-950/5 focus:outline-hidden"
      />
      <label
        htmlFor={id}
        className="pointer-events-none absolute top-6 left-6 -mt-3 origin-left text-base/6 text-neutral-500 transition-all duration-200 peer-not-placeholder-shown:-translate-y-2 peer-not-placeholder-shown:scale-75 peer-not-placeholder-shown:font-semibold peer-not-placeholder-shown:text-neutral-950 peer-focus:-translate-y-2 peer-focus:scale-75 peer-focus:font-semibold peer-focus:text-neutral-950"
      >
        {label}
      </label>
      {errors[name] && (
        <p
          id={`${id}-error`}
          className="mt-1 text-sm text-red-500"
          role="alert"
        >
          {errors[name].message}
        </p>
      )}
    </div>
  )
}

function RadioInput({ label, register, name, value, ...props }) {
  return (
    <label className="flex gap-x-3 cursor-pointer">
      <input
        type="radio"
        value={value}
        {...register(name)}
        {...props}
        aria-label={`${label}`}
        className="h-6 w-6 flex-none appearance-none rounded-full border border-neutral-950/20 outline-hidden checked:border-[0.5rem] checked:border-neutral-950 focus-visible:ring-1 focus-visible:ring-neutral-950 focus-visible:ring-offset-2"
      />
      <span className="text-base/6 text-neutral-950">{label}</span>
    </label>
  )
}

// ---------------------------------------------------------------------------
// Partner program data
// ---------------------------------------------------------------------------

const partnerPrograms = {
  Referral: {
    label: 'Referral',
    tagline: 'Reseller Program',
    color: 'border-amber-200 bg-amber-50',
    iconColor: 'text-amber-600',
    description:
      'Unlock credit as a reseller partner with exclusive early access to Kingdom Kode products and discounted access to our teams. Refer clients, earn rewards, and grow alongside us.',
    perks: [
      'Service credits for every successful referral',
      'Exclusive early access to new Kingdom Kode products',
      'Discounted access to Kingdom Kode teams & services',
      'Co-branded marketing opportunities',
    ],
    icon: (
      <svg
        className="h-6 w-6"
        fill="none"
        viewBox="0 0 24 24"
        strokeWidth="1.5"
        stroke="currentColor"
      >
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          d="M7.5 21 3 16.5m0 0L7.5 12M3 16.5h13.5m0-13.5L21 7.5m0 0L16.5 12M21 7.5H7.5"
        />
      </svg>
    ),
  },
  'Brand Influencer': {
    label: 'Brand Influencer',
    tagline: 'Creator Program',
    color: 'border-pink-200 bg-pink-50',
    iconColor: 'text-pink-600',
    description:
      'For influencers and creators on social media looking to earn extra income while promoting cutting-edge technology. Unlock exclusive merchandise, products, event invitations, and more.',
    perks: [
      'Earn commission on every conversion',
      'Exclusive Kingdom Kode merchandise & swag',
      'Early access to products & features',
      'VIP invitations to Kingdom Kode events',
    ],
    icon: (
      <svg
        className="h-6 w-6"
        fill="none"
        viewBox="0 0 24 24"
        strokeWidth="1.5"
        stroke="currentColor"
      >
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          d="M9.813 15.904 9 18.75l-.813-2.846a4.5 4.5 0 0 0-3.09-3.09L2.25 12l2.846-.813a4.5 4.5 0 0 0 3.09-3.09L9 5.25l.813 2.846a4.5 4.5 0 0 0 3.09 3.09L15.75 12l-2.846.813a4.5 4.5 0 0 0-3.09 3.09ZM18.259 8.715 18 9.75l-.259-1.035a3.375 3.375 0 0 0-2.455-2.456L14.25 6l1.036-.259a3.375 3.375 0 0 0 2.455-2.456L18 2.25l.259 1.035a3.375 3.375 0 0 0 2.455 2.456L21.75 6l-1.036.259a3.375 3.375 0 0 0-2.455 2.456ZM16.894 20.567 16.5 21.75l-.394-1.183a2.25 2.25 0 0 0-1.423-1.423L13.5 18.75l1.183-.394a2.25 2.25 0 0 0 1.423-1.423l.394-1.183.394 1.183a2.25 2.25 0 0 0 1.423 1.423l1.183.394-1.183.394a2.25 2.25 0 0 0-1.423 1.423Z"
        />
      </svg>
    ),
  },
  Education: {
    label: 'Education',
    tagline: 'Educator Program',
    color: 'border-sky-200 bg-sky-50',
    iconColor: 'text-sky-600',
    description:
      'For educators who want to collaborate with us to provide access to our products, share industry knowledge, deliver speeches, and assist with teaching others about technology.',
    perks: [
      'Free or discounted access to Kingdom Kode products for classrooms',
      'Industry guest speakers & workshop support',
      'Co-developed curriculum & learning materials',
      'Internship & mentorship pipeline for students',
    ],
    icon: (
      <svg
        className="h-6 w-6"
        fill="none"
        viewBox="0 0 24 24"
        strokeWidth="1.5"
        stroke="currentColor"
      >
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          d="M4.26 10.147a60.438 60.438 0 0 0-.491 6.347A48.62 48.62 0 0 1 12 20.904a48.62 48.62 0 0 1 8.232-4.41 60.46 60.46 0 0 0-.491-6.347m-15.482 0a50.636 50.636 0 0 0-2.658-.813A59.906 59.906 0 0 1 12 3.493a59.903 59.903 0 0 1 10.399 5.84c-.896.248-1.783.52-2.658.814m-15.482 0A50.717 50.717 0 0 1 12 13.489a50.702 50.702 0 0 1 7.74-3.342M6.75 15a.75.75 0 1 0 0-1.5.75.75 0 0 0 0 1.5Zm0 0v-3.675A55.378 55.378 0 0 1 12 8.443m-7.007 11.55A5.981 5.981 0 0 0 6.75 15.75v-1.5"
        />
      </svg>
    ),
  },
  'Fund Raising': {
    label: 'Fund Raising',
    tagline: 'Investor Program',
    color: 'border-emerald-200 bg-emerald-50',
    iconColor: 'text-emerald-600',
    description:
      'For investors who would like to participate in the projects we are funding through Eden Kode, our investor services product, to capture cutting-edge technology innovations.',
    perks: [
      'Access to curated deal flow via Eden Kode',
      'Priority participation in funded projects',
      'Quarterly investor updates & reporting',
      "Connection to Eden Kode's network of founders",
    ],
    icon: (
      <svg
        className="h-6 w-6"
        fill="none"
        viewBox="0 0 24 24"
        strokeWidth="1.5"
        stroke="currentColor"
      >
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          d="M12 6v12m-3-2.818.879.659c1.171.879 3.07.879 4.242 0 1.172-.879 1.172-2.303 0-3.182C13.536 12.219 12.768 12 12 12c-.725 0-1.45-.22-2.003-.659-1.106-.879-1.106-2.303 0-3.182s2.9-.879 4.006 0l.415.33M21 12a9 9 0 1 1-18 0 9 9 0 0 1 18 0Z"
        />
      </svg>
    ),
  },
  Sponsorship: {
    label: 'Sponsorship',
    tagline: 'Sponsor Program',
    color: 'border-violet-200 bg-violet-50',
    iconColor: 'text-violet-600',
    description:
      'For those who would like to support Kingdom Kode and the global efforts we are making to help businesses and communities across the globe. Sponsors unlock exclusive rewards and recognition.',
    perks: [
      'Limited edition Kingdom Kode merchandise',
      'Service credits toward any Kingdom Kode offering',
      'Exclusive access to private events & conferences',
      'Public recognition on our website & social channels',
    ],
    icon: (
      <svg
        className="h-6 w-6"
        fill="none"
        viewBox="0 0 24 24"
        strokeWidth="1.5"
        stroke="currentColor"
      >
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          d="M21 8.25c0-2.485-2.099-4.5-4.688-4.5-1.935 0-3.597 1.126-4.312 2.733-.715-1.607-2.377-2.733-4.313-2.733C5.1 3.75 3 5.765 3 8.25c0 7.22 9 12 9 12s9-4.78 9-12Z"
        />
      </svg>
    ),
  },
}

const companyStages = [
  'Pre-idea / Concept',
  'MVP / Early Stage',
  'Seed / Pre-Seed',
  'Series A',
  'Growth / Scale',
  'Enterprise',
]

// ---------------------------------------------------------------------------
// Program info panel
// ---------------------------------------------------------------------------

function ProgramInfoPanel({ programKey }) {
  const program = partnerPrograms[programKey]
  if (!program) return null

  return (
    <motion.div
      key={programKey}
      initial={{ opacity: 0, height: 0 }}
      animate={{ opacity: 1, height: 'auto' }}
      exit={{ opacity: 0, height: 0 }}
      transition={{ duration: 0.3, ease: 'easeInOut' }}
      className="overflow-hidden"
    >
      <div className={`mt-4 rounded-xl border p-5 ${program.color}`}>
        <div className="flex items-center gap-3">
          <div className={program.iconColor}>{program.icon}</div>
          <div>
            <p className="font-display text-sm font-semibold text-neutral-950">
              {program.label}
            </p>
            <p className="text-xs text-neutral-500">{program.tagline}</p>
          </div>
        </div>
        <p className="mt-3 text-sm leading-relaxed text-neutral-700">
          {program.description}
        </p>
        <div className="mt-4">
          <p className="text-xs font-semibold uppercase tracking-wide text-neutral-500">
            What you unlock
          </p>
          <ul className="mt-2 space-y-1.5">
            {program.perks.map((perk) => (
              <li
                key={perk}
                className="flex items-start gap-2 text-sm text-neutral-700"
              >
                <svg
                  className="mt-0.5 h-4 w-4 shrink-0 text-neutral-400"
                  fill="none"
                  viewBox="0 0 24 24"
                  strokeWidth="2"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    d="m4.5 12.75 6 6 9-13.5"
                  />
                </svg>
                {perk}
              </li>
            ))}
          </ul>
        </div>
      </div>
    </motion.div>
  )
}

// ---------------------------------------------------------------------------
// Shared select styles
// ---------------------------------------------------------------------------

const selectClassName =
  'mt-4 block w-full appearance-none rounded-lg border border-neutral-300 bg-white px-4 py-3 pr-10 text-base text-neutral-950 ring-4 ring-transparent transition focus:border-neutral-950 focus:ring-neutral-950/5 focus:outline-hidden'

const selectChevronStyle = {
  backgroundImage:
    "url(\"data:image/svg+xml,%3csvg xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 20 20'%3e%3cpath stroke='%236b7280' stroke-linecap='round' stroke-linejoin='round' stroke-width='1.5' d='M6 8l4 4 4-4'/%3e%3c/svg%3e\")",
  backgroundPosition: 'right 0.75rem center',
  backgroundRepeat: 'no-repeat',
  backgroundSize: '1.25em 1.25em',
}

// ---------------------------------------------------------------------------
// Form
// ---------------------------------------------------------------------------

export default function PartnershipForm() {
  const recaptcha = useRef()
  const [formStatus, setFormStatus] = useState('idle')
  const [errorMessage, setErrorMessage] = useState('')
  const [selectedProgram, setSelectedProgram] = useState('')

  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
    reset,
  } = useForm({
    defaultValues: {
      partnerInterest: '',
      name: '',
      email: '',
      company: '',
      companySize: '',
      jobTitle: '',
      phone: '',
      country: '',
      companyStage: '',
      specialization: '',
      portfolioUrl: '',
      techStack: '',
      whyKingdomKode: '',
    },
  })

  const isFundRaising = selectedProgram === 'Fund Raising'

  const onSubmit = async (data) => {
    try {
      setFormStatus('sending')
      setErrorMessage('')

      const captchaValue = recaptcha.current.getValue()

      if (!captchaValue) {
        setErrorMessage('Please verify the reCAPTCHA!')
        setFormStatus('error')
        return
      }

      const captchaRes = await fetch('/api/captcha/verify', {
        method: 'POST',
        body: JSON.stringify({ captchaValue }),
        headers: { 'Content-Type': 'application/json' },
      })

      const captchaData = await captchaRes.json()

      if (!captchaData.success) {
        setErrorMessage('Failed to verify reCAPTCHA. Please try again.')
        setFormStatus('error')
        return
      }

      // Strip companyStage if not Fund Raising
      if (data.partnerInterest !== 'Fund Raising') {
        data.companyStage = null
      }

      const response = await fetch('/api/partner-lead', {
        method: 'POST',
        body: JSON.stringify(data),
        headers: { 'Content-Type': 'application/json' },
      })

      const result = await response.json()

      if (response.ok) {
        posthog.capture('partnership_application_submitted', {
          partner_program: data.partnerInterest,
          company_stage: data.companyStage || undefined,
        })
        setFormStatus('success')
        setTimeout(() => {
          reset()
          setSelectedProgram('')
          setFormStatus('idle')
        }, 4000)
      } else {
        setErrorMessage(result.error || 'Failed to submit. Please try again.')
        setFormStatus('error')
      }
    } catch (error) {
      console.error('Error submitting partnership form:', error)
      setErrorMessage('An unexpected error occurred. Please try again.')
      setFormStatus('error')
    }
  }

  const clearError = () => {
    setErrorMessage('')
    setFormStatus('idle')
  }

  // Sync select with react-hook-form and local state
  const partnerInterestRegistration = register('partnerInterest', {
    required: 'Please select a partner program',
  })

  const handleProgramChange = (e) => {
    partnerInterestRegistration.onChange(e)
    setSelectedProgram(e.target.value)
  }

  return (
    <FadeIn>
      <form
        onSubmit={handleSubmit(onSubmit)}
        aria-label="Partnership inquiry form"
      >
        <h2 className="font-display text-base font-semibold text-neutral-950">
          Partnership Inquiry
        </h2>
        <p className="mt-2 text-sm text-neutral-600">
          Tell us about your needs and we&apos;ll connect you with the right
          program from our network.
        </p>

        {errorMessage && (
          <AlertMessage
            message={errorMessage}
            type="error"
            onClose={clearError}
          />
        )}

        <AnimatePresence>
          {formStatus === 'success' && (
            <motion.div
              initial={{ opacity: 0, y: -10 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -10 }}
              className="mt-4 rounded-lg border border-emerald-200 bg-emerald-50 p-4 text-sm font-medium text-emerald-800"
            >
              Your inquiry has been submitted! Our team will review your
              application and reach out shortly.
            </motion.div>
          )}
        </AnimatePresence>

        {/* ----------------------------------------------------------------- */}
        {/* Partner Program Select — FIRST field */}
        {/* ----------------------------------------------------------------- */}
        <div className="mt-6 rounded-t-2xl border border-neutral-300 px-6 py-8">
          <label
            htmlFor="partnerInterest"
            className="block text-base/6 font-medium text-neutral-500"
          >
            Which partner program are you interested in?
          </label>
          <select
            id="partnerInterest"
            {...partnerInterestRegistration}
            onChange={handleProgramChange}
            className={selectClassName}
            style={selectChevronStyle}
          >
            <option value="">Select a program...</option>
            {Object.keys(partnerPrograms).map((key) => (
              <option key={key} value={key}>
                {partnerPrograms[key].label} &mdash;{' '}
                {partnerPrograms[key].tagline}
              </option>
            ))}
          </select>
          {errors.partnerInterest && (
            <p className="mt-2 text-sm text-red-500" role="alert">
              {errors.partnerInterest.message}
            </p>
          )}

          <AnimatePresence mode="wait">
            {selectedProgram && partnerPrograms[selectedProgram] && (
              <ProgramInfoPanel programKey={selectedProgram} />
            )}
          </AnimatePresence>
        </div>

        {/* ----------------------------------------------------------------- */}
        {/* Text fields */}
        {/* ----------------------------------------------------------------- */}
        <div className="isolate -mt-px -space-y-px bg-white/50">
          <TextInput
            label="Name"
            name="name"
            autoComplete="name"
            register={register}
            validationRules={{ required: 'Name is required' }}
            errors={errors}
          />
          <TextInput
            label="Email"
            type="email"
            name="email"
            autoComplete="email"
            register={register}
            validationRules={{
              required: 'Email is required',
              pattern: {
                value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                message: 'Invalid email address',
              },
            }}
            errors={errors}
          />
          <TextInput
            label="Company Name"
            name="company"
            autoComplete="organization"
            register={register}
            errors={errors}
          />
          <TextInput
            label="Company Size"
            name="companySize"
            placeholder="e.g. 1-10, 11-50, 51-200, 200+"
            register={register}
            errors={errors}
          />
          <TextInput
            label="Job Title"
            name="jobTitle"
            placeholder="e.g. CEO, CTO, Founder"
            autoComplete="organization-title"
            register={register}
            errors={errors}
          />
          <TextInput
            label="Phone Number"
            type="tel"
            name="phone"
            autoComplete="tel"
            register={register}
            errors={errors}
          />
          <TextInput
            label="Country"
            name="country"
            autoComplete="country-name"
            register={register}
            errors={errors}
          />
          <TextInput
            label="Specialization"
            name="specialization"
            placeholder="e.g. SaaS, FinTech, E-Commerce"
            register={register}
            errors={errors}
          />
          <TextInput
            label="Portfolio URL"
            type="url"
            name="portfolioUrl"
            placeholder="https://yoursite.com"
            autoComplete="url"
            register={register}
            validationRules={{
              pattern: {
                value: /^(https?:\/\/)?[\w.-]+\.[a-z]{2,}(\/.*)?$/i,
                message: 'Please enter a valid URL',
              },
            }}
            errors={errors}
          />
          <TextInput
            label="Tech Stack"
            name="techStack"
            placeholder="e.g. React, Node.js, Python"
            register={register}
            errors={errors}
          />
          <TextAreaInput
            label="Why Kingdom Kode?"
            name="whyKingdomKode"
            placeholder="Tell us why you're interested in partnering with Kingdom Kode..."
            rows={4}
            register={register}
            errors={errors}
          />
        </div>

        {/* ----------------------------------------------------------------- */}
        {/* Company Stage — only visible for Fund Raising */}
        {/* ----------------------------------------------------------------- */}
        <AnimatePresence>
          {isFundRaising && (
            <motion.div
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: 'auto' }}
              exit={{ opacity: 0, height: 0 }}
              transition={{ duration: 0.3, ease: 'easeInOut' }}
              className="overflow-hidden"
            >
              <div className="-mt-px border border-neutral-300 rounded-b-2xl px-6 py-8">
                <fieldset>
                  <legend className="text-base/6 font-medium text-neutral-500">
                    What stage is your company at?
                  </legend>
                  <div
                    className="mt-6 grid grid-cols-1 gap-6 sm:grid-cols-2"
                    role="radiogroup"
                    aria-label="Company stage selection"
                  >
                    {companyStages.map((stage) => (
                      <RadioInput
                        key={stage}
                        label={stage}
                        name="companyStage"
                        value={stage}
                        register={register}
                      />
                    ))}
                  </div>
                </fieldset>
              </div>
            </motion.div>
          )}
        </AnimatePresence>

        {/* ----------------------------------------------------------------- */}
        {/* Submit */}
        {/* ----------------------------------------------------------------- */}
        <div className="mt-10 flex w-full flex-col items-center gap-3">
          <div aria-label="reCAPTCHA verification">
            <ReCAPTCHA
              ref={recaptcha}
              sitekey={process.env.NEXT_PUBLIC_RECAPTCHA_SITE_KEY}
            />
          </div>

          <div className="flex w-full">
            <ContactButton
              type="submit"
              disabled={
                isSubmitting ||
                formStatus === 'sending' ||
                formStatus === 'success'
              }
              status={formStatus}
              aria-label="Submit partnership inquiry"
              id="partnership-form-submit"
            />
          </div>

          <p className="mt-4 text-center text-sm text-neutral-500">
            By submitting this form, you agree to our{' '}
            <a
              href="/tos"
              target="_blank"
              rel="noopener noreferrer"
              className="text-neutral-900 hover:underline"
            >
              Terms of Service
            </a>{' '}
            and{' '}
            <a
              href="/privacy-policy"
              target="_blank"
              rel="noopener noreferrer"
              className="text-neutral-900 hover:underline"
            >
              Privacy Policy
            </a>
            .
          </p>
        </div>
      </form>
    </FadeIn>
  )
}
